"use client";

import { IPriceTextProps } from "@faire/deprecated-retailer-visitor-shared/views/product/Price/PriceProps";
import { Decorative } from "@faire/design-tokens";
import { Core } from "@faire/slate/foundation/color";
import {
  DEPRECATED_backwardsCompatibleValues,
  useStrictLocalization,
} from "@faire/web/common/localization";
import { Row } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { Span as TypographySpan } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { type JSX } from "react";
import styled from "styled-components";

const RecommendedRetailPricePromoText: React.FC<IPriceTextProps> = ({
  originalPrice,
  wholesalePrice,
  retailPrice,
}) => {
  const renderPromoFormatting = (chunk: JSX.Element) => (
    <TypographySpan
      color={Decorative.shade.orange}
      variant="subheadingSansMedium"
      style={{
        marginRight: getSpacing("1/2x"),
      }}
    >
      {chunk}
    </TypographySpan>
  );
  return (
    <>
      <StrictLocalMsg
        id="9UNdnA"
        defaultMessage="<PromoFormatting>{wholesaleAmountMoney}</PromoFormatting>"
        values={{
          PromoFormatting: renderPromoFormatting,
          wholesaleAmountMoney: wholesalePrice,
        }}
        description={{
          text: " discounted wholesale price through product-level promotion",
        }}
      />
      <StrictLocalMsg
        id="75mZPc"
        defaultMessage="<StrikeThroughFormatting>{originalAmountMoney}</StrikeThroughFormatting>"
        values={{
          StrikeThroughFormatting: renderStrikeThroughFormatting,
          TextFormatting: renderRegularChunk,
          originalAmountMoney: originalPrice,
        }}
        description={{
          text: "Original wholesale price of the product with dollar sign",
        }}
      />
      <StrictLocalMsg
        id="M0Bha1"
        defaultMessage="<TextFormatting>RRP {retailAmountMoney}</TextFormatting>"
        values={{
          TextFormatting: renderRegularChunk,
          retailAmountMoney: retailPrice,
        }}
        description={{
          text: "manufacturer's suggested retail price of the product with dollar sign",
        }}
      />
    </>
  );
};

const PromoText: React.FC<IPriceTextProps> = ({
  originalPrice,
  wholesalePrice,
  retailPrice,
}) => {
  const renderPromoFormatting = (chunk: JSX.Element) => (
    <TypographySpan
      color={Decorative.shade.orange}
      variant="subheadingSansMedium"
      style={{
        marginRight: getSpacing("1/2x"),
      }}
    >
      {chunk}
    </TypographySpan>
  );

  return (
    <>
      <StrictLocalMsg
        id="1cmlq3"
        defaultMessage="<PromoFormatting>{wholesaleAmountMoney}</PromoFormatting>"
        values={{
          PromoFormatting: renderPromoFormatting,
          wholesaleAmountMoney: wholesalePrice ?? "",
        }}
        description={{
          text: " discounted wholesale price through product-level promotion with dollar sign",
        }}
      />
      <StrictLocalMsg
        id="75mZPc"
        defaultMessage="<StrikeThroughFormatting>{originalAmountMoney}</StrikeThroughFormatting>"
        values={{
          StrikeThroughFormatting: renderStrikeThroughFormatting,
          originalAmountMoney: originalPrice,
        }}
        description={{
          text: "Original wholesale price of the product with dollar sign",
        }}
      />
      <div>
        <StrictLocalMsg
          id="8EOVV1"
          defaultMessage="<TextFormatting>MSRP {retailAmountMoney}</TextFormatting>"
          values={{
            TextFormatting: renderRegularLabelChunk,
            retailAmountMoney: retailPrice,
          }}
          description={{
            text: "manufacturer's suggested retail price of the product with dollar sign",
          }}
        />
      </div>
    </>
  );
};

const RecommendedRetailPriceText: React.FC<
  Pick<IPriceTextProps, "wholesalePrice" | "retailPrice" | "small">
> = ({ wholesalePrice, retailPrice, small }) => {
  return (
    <>
      <StrictLocalMsg
        id="+vpfaz"
        defaultMessage="<BoldFormatting>{wholesaleAmountMoney}</BoldFormatting>"
        values={{
          BoldFormatting: small
            ? renderParagraphBoldFormatting
            : renderSubHeadingBoldFormatting,
          wholesaleAmountMoney: wholesalePrice ?? "",
        }}
        description={{
          text: "Wholesale price for UK retailers with dollar sign",
        }}
      />
      <StrictLocalMsg
        id="HYugD1"
        defaultMessage="<TextFormatting>RRP {retailAmountMoney}</TextFormatting>"
        values={{
          TextFormatting: renderRegularLabelChunkGray,
          retailAmountMoney: retailPrice,
        }}
        description={{
          text: "manufacturer's suggested retail price of the product for UK retailers with dollar sign",
        }}
      />
    </>
  );
};

const PriceText: React.FC<
  Pick<
    IPriceTextProps,
    "wholesalePrice" | "retailPrice" | "isTilePricing" | "priceTextSurface"
  >
> = ({ wholesalePrice, retailPrice }) => {
  return (
    <>
      <StrictLocalMsg
        id="kQgUIs"
        defaultMessage="<WholesaleTextFormatting>{wholesaleAmountMoney}</WholesaleTextFormatting>"
        values={{
          WholesaleTextFormatting: renderParagraphBoldFormatting,
          wholesaleAmountMoney: wholesalePrice ?? "",
        }}
        description={{
          text: "Wholesale price of the product with dollar sign",
        }}
      />
      <div>
        <StrictLocalMsg
          id="8EOVV1"
          defaultMessage="<TextFormatting>MSRP {retailAmountMoney}</TextFormatting>"
          values={{
            TextFormatting: renderRegularLabelChunk,
            retailAmountMoney: retailPrice,
          }}
          description={{
            text: "manufacturer's suggested retail price of the product with dollar sign",
          }}
        />
      </div>
    </>
  );
};

const PriceBase: React.FC<IPriceTextProps> = (props) => {
  const { strictLocalize } = useStrictLocalization();

  if (props.showProductPromotions) {
    return (
      <Row
        align="baseline"
        data-test-id="promotion-text"
        className="flex-wrap gap-x-[4px] gap-y-[0px] leading-[0px]"
        aria-label={strictLocalize(
          {
            id: "YFDyy0",
            defaultMessage:
              "original price is {originalAmountMoney}, discounted price is {wholesaleAmountMoney}",
            description: {
              text: "aria-label for stating price",
              img: "https://cdn.faire.com/fastly/357091ed2d43c92e5e558a48f9462f4de5ac89689c208bbc460682d9a2552163.png",
            },
          },
          DEPRECATED_backwardsCompatibleValues({
            originalAmountMoney: props.originalPrice,
            wholesaleAmountMoney: props.wholesalePrice,
          })
        )}
      >
        {props.isUKRetailer ? (
          <RecommendedRetailPricePromoText {...props} />
        ) : (
          <PromoText {...props} />
        )}
      </Row>
    );
  } else {
    return (
      <Row
        align="baseline"
        data-test-id="product-tile-msrp"
        className="flex-wrap gap-x-[4px] gap-y-[0px] leading-[0px]"
      >
        {props.isUKRetailer ? (
          <RecommendedRetailPriceText {...props} />
        ) : (
          <PriceText {...props} />
        )}
      </Row>
    );
  }
};

export const ProminentPrice: React.FC<IPriceTextProps> = (props) => {
  return <PriceBase {...props} />;
};

const renderRegularChunk = (chunk: JSX.Element) => (
  <TypographySpan variant="paragraphSansRegular">{chunk}</TypographySpan>
);

const renderRegularLabelChunk = (chunk: JSX.Element) => (
  <TypographySpan variant="labelSansRegular">{chunk}</TypographySpan>
);

const renderRegularLabelChunkGray = (chunk: JSX.Element) => (
  <TypographySpan variant="labelSansRegular" color={Core.text.subdued}>
    {chunk}
  </TypographySpan>
);

const renderStrikeThroughFormatting = (chunk: JSX.Element) => (
  <StyledStrikethrough>{chunk}</StyledStrikethrough>
);

const renderParagraphBoldFormatting = (chunk: JSX.Element) => (
  <TypographySpan variant="subheadingSansMedium">{chunk}</TypographySpan>
);

const renderSubHeadingBoldFormatting = (chunk: JSX.Element) => (
  <TypographySpan variant="subheadingSansMedium">{chunk}</TypographySpan>
);

const StyledStrikethrough = styled(TypographySpan).attrs({
  variant: "paragraphSansRegular",
})`
  text-decoration: line-through;
`;
